// src/utils/segmentiGiornata.js
// ═══════════════════════════════════════════════════════════════════════════
// SEGMENTI DI UNA GIORNATA DEL CARTELLINO — un solo calcolo per tutta l'app (ottobre 2026)
// ═══════════════════════════════════════════════════════════════════════════
// Le timbrature di LAVORO si accoppiano tra loro (entrata → uscita, in ordine di orario) e
// quelle SPECIALI (Ferie, Permesso, Malattia) tra loro, voce per voce. Se un segmento
// speciale cade dentro l'orario di lavoro (es. lavoro 08:00–17:00 e permesso 10:00–12:00),
// i suoi minuti si TOLGONO da quelli lavorati: 9 h − 2 h = 7 h di lavoro e 2 h di permesso.
//
// Perché: prima tutte le timbrature si accoppiavano in un'unica fila; con un permesso a ore
// scritto dentro una giornata timbrata 8–17 l'entrata delle 8 veniva persa e con lei le ore
// di lavoro. Le giornate timbrate in modo ordinato (lavoro 8–10, permesso 10–12, lavoro
// 12–17, oppure solo lavoro, oppure solo una voce speciale) danno ESATTAMENTE gli stessi
// segmenti e minuti di prima (verificato con prove automatiche, vedi demo/prove/).
//
// Ogni segmento: { entrata, uscita, commessaNome, commessaId, minuti, speciale, tipo,
// minutiTolti }. Per il lavoro `minuti` è già al netto delle sovrapposizioni con le voci
// speciali (minutiTolti dice quanti ne sono stati tolti); entrata/uscita restano quelle
// timbrate. La pausa pranzo resta compito di chi usa il risultato, come prima.
// ═══════════════════════════════════════════════════════════════════════════

export const minutiDaOra = (ora) => {
  if (!ora || typeof ora !== "string" || !ora.includes(":")) return null;
  const [h, m] = ora.split(":").map(Number);
  return Number.isFinite(h) && Number.isFinite(m) ? h * 60 + m : null;
};

// in ordine di orario; a parità di orario prima l'uscita (un segmento che finisce alle 10:00
// e il successivo che comincia alle 10:00), qualunque sia l'ordine in cui sono state salvate
const ordina = (lista) => [...lista].sort((a, b) => (a.ora || "").localeCompare(b.ora || "") || (a.tipo === "uscita" ? -1 : 0) - (b.tipo === "uscita" ? -1 : 0));

/** Accoppia entrata → uscita in ordine di orario; un'entrata senza uscita resta «aperta». */
function accoppia(lista, stessaVoce) {
  const segmenti = [];
  let aperta = null;
  for (const t of ordina(lista)) {
    if (t.tipo === "entrata") { aperta = t; continue; }
    if (t.tipo !== "uscita" || !aperta) continue;
    if (stessaVoce && (t.commessaNome || "") !== (aperta.commessaNome || "")) { aperta = null; continue; }
    const a = minutiDaOra(aperta.ora), b = minutiDaOra(t.ora);
    if (a != null && b != null && b > a) segmenti.push({ entrata: aperta.ora, uscita: t.ora, da: a, a: b, origE: aperta, origU: t });
    // voce speciale con orari non validi (dati vecchi): valgono le ore indicate, come prima
    else if (stessaVoce && Number(t.oreForzate) > 0) segmenti.push({ entrata: aperta.ora, uscita: t.ora, da: 0, a: 0, forzati: Math.round(Number(t.oreForzate) * 60), origE: aperta, origU: t });
    aperta = null;
  }
  return { segmenti, aperta };
}

/** Minuti di [da, a) coperti dall'unione degli intervalli speciali. */
function sovrapposti(da, a, intervalli) {
  const pezzi = intervalli.map(x => [Math.max(da, x.da), Math.min(a, x.a)]).filter(([p, q]) => q > p).sort((x, y) => x[0] - y[0]);
  let tot = 0, fine = -1;
  for (const [p, q] of pezzi) {
    if (q <= fine) continue;
    tot += q - Math.max(p, fine);
    fine = q;
  }
  return tot;
}

/**
 * Segmenti della giornata e stato di completezza.
 * @returns {{ segmenti: object[], apertaLavoro: object|null, apertaSpeciale: object|null }}
 */
export function segmentiGiornata(timbrature) {
  const tutte = Array.isArray(timbrature) ? timbrature : [];
  const lavoro = tutte.filter(t => !t.speciale);
  const speciali = tutte.filter(t => t.speciale);

  // le voci speciali si accoppiano per voce (Ferie con Ferie, Permesso con Permesso)
  const perVoce = new Map();
  speciali.forEach(t => { const k = t.commessaNome || ""; if (!perVoce.has(k)) perVoce.set(k, []); perVoce.get(k).push(t); });
  const segSpeciali = [];
  let apertaSpeciale = null;
  for (const lista of perVoce.values()) {
    const r = accoppia(lista, true);
    segSpeciali.push(...r.segmenti);
    if (r.aperta) apertaSpeciale = r.aperta;
  }
  const rl = accoppia(lavoro, false);

  const segmenti = [
    ...rl.segmenti.map(s => {
      const tolti = segSpeciali.length ? sovrapposti(s.da, s.a, segSpeciali) : 0;
      return { entrata: s.entrata, uscita: s.uscita, commessaNome: s.origE.commessaNome || "", commessaId: s.origE.commessaId || "",
        minuti: (s.a - s.da) - tolti, minutiTolti: tolti, speciale: false, tipo: "lavoro" };
    }),
    ...segSpeciali.map(s => ({ entrata: s.entrata, uscita: s.uscita, commessaNome: s.origE.commessaNome || "", commessaId: "",
      minuti: s.forzati != null ? s.forzati : s.a - s.da, minutiTolti: 0, speciale: true, tipo: s.origE.commessaNome || "" })),
  ].sort((x, y) => (x.entrata || "").localeCompare(y.entrata || "") || (x.speciale ? 1 : 0) - (y.speciale ? 1 : 0));

  return { segmenti, apertaLavoro: rl.aperta, apertaSpeciale };
}

/** Minuti di lavoro lordi della giornata (prima della pausa pranzo), al netto delle voci speciali. */
export function minutiLavoroGiornata(timbrature) {
  return segmentiGiornata(timbrature).segmenti.filter(s => !s.speciale).reduce((t, s) => t + s.minuti, 0);
}
